/**
 * External dependencies.
 */
import type { GetStaticProps, NextPage } from "next";

/**
 * Internal dependencies.
 */
import {
  fetchAPI,
  getLocale,
  getLocalizationData,
  getStrapiMedia,
  handleBaseLocales,
  loadTranslation,
} from "utils";
import Layout from "../../layout/layout";
import TealBox from "components/teal-box/teal-box";
import ThemeListing from "components/theme-listing/theme-listing";
import SchemaSet from "components/schema-set/schema-set";
import Markdown from "components/markdown/markdown";
import SEO from "components/seo/seo";
import HreflangControlled from "blocks/hreflang/hreflang-controlled";

const ThemeAll: NextPage = (prop: any) => {
  const locales = prop?.locales;
  const { Title, Content } = prop?.header?.attributes ?? {};

  const themeListingArr = prop.data?.map(({ attributes }: any) => ({
    ...attributes,
    name: attributes.Title,
    price: attributes.Price,
    image: getStrapiMedia(attributes.PreviewImage),
    colors: attributes.Variants.map((item: any) => item.ColorHexCode),
  }));

  return (
    <Layout title="Themes" desc="Themes" light notice hreflang={false}>
      <HreflangControlled localizations={locales} />
      <SEO data={prop.header?.attributes.SEO} />
      <SchemaSet
        schemaData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Themes",
          mainEntity: [
            ...(themeListingArr?.map((item: any) => ({
              "@type": "WebPage",
              name: item.name,
            })) ?? []),
          ],
        }}
      />
      <TealBox maxWidth="650px">
        <h3>{Title}</h3>
        <Markdown content={Content} />
      </TealBox>
      <ThemeListing themes={themeListingArr} />
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const translation = await loadTranslation(
    ctx.locale!,
    process.env.NODE_ENV === "production"
  );
  const locale = getLocale(ctx.locale);

  const { data: themeHeaderData } = await fetchAPI("/theme-header", {
    populate: {
      SEO: {
        populate: "*",
      },
      localizations: "*",
    },
    locale: locale,
  });
  const { data } = await fetchAPI("/themes", {
    populate: {
      PreviewImage: "*",
      Variants: {
        populate: ["*", "Images"],
      },
      localizations: "*",
    },
    locale: locale,
  });

  return {
    props: {
      translation,
      data,
      header: themeHeaderData,
      locales: handleBaseLocales(ctx.locales),
      localizationData: getLocalizationData(themeHeaderData, "all"),
    },
    revalidate: 10,
  };
};

export default ThemeAll;
